import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import * as Yup from "yup";
import { Formik, Form, Field, FieldArray, getIn } from "formik";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker, } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import IntlMessages from "../../../../../utils/IntlMessages";
import {
  getLabel,
  getOverlineText,
  getIntlTranslation,
} from "../../../../../utils/helper";
import {
  Box,
  Grid,
  Autocomplete,
  TextField,
  Divider,
} from "@mui/material";
import parse from "autosuggest-highlight/parse";
import match from "autosuggest-highlight/match";
import CustomIconButton from "../../../../../components/buttons/icons-buttons/CustomIconButton";
import AddIcon from "@mui/icons-material/Add";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import dayjs from 'dayjs';

const validationSchema = Yup.object().shape({
  subAssets: Yup.array().of(
    Yup.object().shape({
      name: Yup.string()
        .min(1, <IntlMessages id="required_field" />)
        .required(<IntlMessages id="required_field" />),
      constructionYear: Yup.string()
        .min(1, <IntlMessages id="required_field" />)
        .required(<IntlMessages id="required_field" />),
      type: Yup.object().shape({
        title: Yup.string()
          .min(1, <IntlMessages id="required_field" />)
          .required(<IntlMessages id="required_field" />),
        value: Yup.string()
          .nullable()
          .required(<IntlMessages id="required_field" />)
          .required(<IntlMessages id="required_field" />),
      }),
    })
  ),
});
const SubAssetCreateForm = (props) => {
  const scope = useSelector((state) => state.container.scope);
  const [fullAddress, setFullAddress] = useState("");

  const createSubAssets = (formProps) => {
    let rows = [];
    formProps.form.values.subAssets.forEach((m, i) => {
      rows.push(
        <>
          <Box
            className="d-flex flex-row align-items-center justify-content-start w-100"
            style={{ marginLeft: 15 }}
          >
            <div style={{ width: "20%" }}>
              {getOverlineText("_building")} {i + 1}
            </div>
            <Grid container className="custom-grid-container" justify="start">
              <Grid item md={12} xs={12}>
                <Field
                  name={`subAssets[${i}].name`}
                  id={`subasset_name_${i + 1}`}
                  disabled={props.readOnly}
                  label={getLabel("Name")}
                  as={TextField}
                  multiline
                  size="small"
                  dense="true"
                  className="grid-fields"
                  error={
                    getIn(formProps.form.touched, `subAssets[${i}].name`) &&
                    getIn(formProps.form.errors, `subAssets[${i}].name`)
                  }
                  helperText={
                    getIn(formProps.form.touched, `subAssets[${i}].name`) &&
                    getIn(formProps.form.errors, `subAssets[${i}].name`)
                  }
                />
              </Grid>
              <Grid item md={12} xs={12}>
              <Autocomplete
                    freeSolo
                    id={`subAssets[${i}].building_type`}
                    size="small"
                    value={
                      !!formProps.form.values.subAssets[i].type
                        ? formProps.form.values.subAssets[i].type
                        : ""
                    }
                    getOptionLabel={(option) => {
                      // Value selected with enter, right from the input
                      if (typeof option === "string") {
                        return option;
                      }
                      // Add "xxx" option created dynamically
                      if (option.inputValue) {
                        return option.inputValue;
                      }
                      // Regular option
                      return option.title;
                    }}
                    disableClearable
                    autoComplete={true}
                    blurOnSelect={true}
                    inputValue={formProps.form.values.subAssets[i].type.title}
                    onInputChange={(event, newInputValue) => {
                      if (typeof newInputValue === "string") {
                        formProps.form.setFieldValue(
                          `subAssets[${i}].type`,
                          {
                            title: newInputValue,
                            value: newInputValue,
                          }
                        );
                      }
                    }}
                    options={props.buildingType?.filter(
                      (f) => f.language === scope.projectLanguageCode
                    )}
                    getOptionSelected={(option, value) =>
                      value.value === option.value
                    }
                    onChange={(event, newValue) => {
                      if (typeof newValue === "string") {
                        formProps.form.setFieldValue(
                          `subAssets[${i}].type`,
                          {
                            title: newValue,
                            value: newValue,
                          }
                        );
                      } else if (newValue !== null) {
                        formProps.form.setFieldValue(
                          `subAssets[${i}].type`,
                          newValue
                        );
                      } else {
                        formProps.form.setFieldValue(
                          `subAssets[${i}].type`,
                          {
                            title: "",
                            value: null,
                          }
                        );
                      }
                    }}
                    renderInput={(params) => (
                      <>
                        <TextField
                          {...params}
                          label={getLabel("_building type")}
                          name={`subAssets[${i}].type`}
                          className="grid-fields"
                          size="small"
                          dense="true"
                          error={
                            getIn(
                              formProps.form.touched,
                              `subAssets[${i}].type.value`
                            ) &&
                            getIn(
                              formProps.form.errors,
                              `subAssets[${i}].type.value`
                            )
                          }
                          helperText={
                            getIn(
                              formProps.form.touched,
                              `subAssets[${i}].type.value`
                            ) &&
                            getIn(
                              formProps.form.errors,
                              `subAssets[${i}].type.value`
                            )
                          }
                        />
                      </>
                    )}
                    renderOption={(props, option, { inputValue }) => {
                      const matches = match(option.title, inputValue);
                      const parts = parse(option.title, matches);

                      return (
                        <>
                          <li {...props}>
                            <div>
                              {parts.map((part, index) => (
                                <span
                                  key={index}
                                  style={{
                                    color: part.highlight ? "red" : "black",
                                  }}
                                >
                                  {part.text}
                                </span>
                              ))}
                            </div>
                          </li>
                        </>
                      );
                    }}
                  />
              </Grid>
              <Grid item md={12} xs={12} style={{marginTop:5}} >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    
                    minDate={dayjs(new Date("01/01/1800"))}
                    name={`subAssets[${i}].constructionYear`}
                    views={["year"]}
                    variant="inline"
                    id={`subAssets[${i}].construction_year`}
                    className="grid-fields"
                    label={getIntlTranslation("_Construction Year")}
                    value={!!formProps.form.values.subAssets[i].constructionYear ? 
                      dayjs(new Date(formProps.form.values.subAssets[i].constructionYear,1,1)) 
                      : null
                    }
                    InputAdornmentProps={{ position: "start" }}
                    onChange={(date) => {
                      if (!!date?.year()) {
                        formProps.form.setFieldValue(
                          `subAssets[${i}].constructionYear`,
                          date.year()
                        );
                      } else {
                        formProps.form.setFieldValue(
                          `subAssets[${i}].constructionYear`,
                          null
                        );                        
                      }
                    }}
                    slotProps={{
                      textField: {
                          variant: "outlined",
                          size: "small",
                          style: {width:"100%"},
                          error: getIn(
                            formProps.form.touched,
                            `subAssets[${i}].constructionYear`
                          ) &&
                          getIn(
                            formProps.form.errors,
                            `subAssets[${i}].constructionYear`
                          ),
                          helperText:  (getIn(
                            formProps.form.touched,
                            `subAssets[${i}].constructionYear`
                          ) &&
                          getIn(
                            formProps.form.errors,
                            `subAssets[${i}].constructionYear`
                          ))
                      },
                      inputAdornment:{
                        position:"start"
                      },
                  }}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>
            {!props.editDialog && (
              <>
                <Divider
                  orientation="vertical"
                  flexItem
                  style={{ margin: "5px 5px", backgroundColor: "#254a9a" }}
                />
                <div
                  className="d-flex align-items-center justify-content-center"
                  style={{
                    width: "10%",
                    height: "100%",
                  }}
                >
                  <CustomIconButton
                    id={`add_row_button_${i}`}
                    icon={
                      <AddIcon style={{ color: "#d08f8e", fontSize: 25 }} />
                    }
                    permission={null}
                    handleClick={(e) => {
                      formProps.push({
                        id: Date.now(),
                        name: "",
                        type: { title: "", value: "" },
                        order: i + 1 + 1,
                        constructionYear: new Date(),
                        deleted: false,
                      });
                    }}
                    tooltip={{
                      title: getIntlTranslation("tooltip.add-row"),
                    }}
                    style={{
                      borderRadius: 0,
                    }}
                    aria-describedby={`add_row_button_${i}`}
                  />
                  {formProps.form.values.subAssets.length > 1 && (
                    <CustomIconButton
                      id={`delete_row_button_${i}`}
                      icon={
                        <DeleteForeverIcon
                          style={{ color: "#f50057", fontSize: 25 }}
                        />
                      }
                      permission={null}
                      handleClick={(e) => {
                        formProps.remove(i);
                      }}
                      tooltip={{
                        title: getIntlTranslation("tooltip.delete"),
                      }}
                      style={{
                        borderRadius: 0,
                      }}
                      aria-describedby={`delete_row_button_${i}`}
                    />
                  )}
                </div>
              </>
            )}
          </Box>
          {i < formProps.form.values.subAssets.length - 1 && (
            <Divider component="div" style={{ padding: 5, width: "98%" }} />
          )}
        </>
      );
    });
    return rows;
  };

  return (
    <Box
      style={{ overflowY: "auto", marginTop: 5 }}
      className={"custom-scrollBar"}
    >
      <Card>
        <CardContent>
          <Formik
            enableReinitialize
            initialValues={props.intialFormData}
            validationSchema={validationSchema}
            onSubmit={(values) => props.handleSave(values)}
            innerRef={props.formRef}
          >
            {({
              values,
              setFieldValue,
              errors,
              touched,
              handleChange,
              isSubmitting,
            }) => (
              <>
                <Form>
                  <Grid item md={12} xs={12}>
                    {getOverlineText("_Sub-Asset Information")}
                  </Grid>
                  <FieldArray name="subAssets">
                    {(fieldArrayProps) => {
                      return createSubAssets(fieldArrayProps);
                    }}
                  </FieldArray>
                </Form>
              </>
            )}
          </Formik>
        </CardContent>
      </Card>
    </Box>
  );
};

SubAssetCreateForm.defaultProps = {
  editDialog: false,
};

SubAssetCreateForm.propTypes = {
  intialFormData: PropTypes.object,
  handleSave: PropTypes.func,
  formRef: PropTypes.object,
  buildingType: PropTypes.array,
  editDialog: PropTypes.bool,
  generalExist: PropTypes.bool,
};

export default SubAssetCreateForm;
